import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Truck, MapPin, Loader2, Search, CheckCircle2 } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';

export const FREE_SHIPPING = 60;

const ShippingSelector = ({ subtotal, value, onChange }) => {
  const [methods, setMethods] = useState(null);
  const [zip, setZip] = useState('');
  const [points, setPoints] = useState(null);
  const [searching, setSearching] = useState(false);
  const free = subtotal >= FREE_SHIPPING;

  useEffect(() => {
    api.get('/shipping/methods').then((r) => setMethods(r.data)).catch(() => setMethods([]));
  }, []);

  const select = (m) => {
    onChange({ method: m, point: m.mode === 'PARCEL_POINT' ? value?.point : null });
  };

  const search = async () => {
    if (zip.trim().length < 4) { toast.error('Entrez un code postal'); return; }
    setSearching(true);
    try {
      const { data } = await api.post('/shipping/parcel-points', { postalCode: zip.trim(), city: '' });
      setPoints(data.content || []);
      if (!data.content?.length) toast.error('Aucun point relais trouvé pour ce code postal');
    } catch {
      toast.error('Recherche de points relais momentanément indisponible');
      setPoints([]);
    } finally {
      setSearching(false);
    }
  };

  const fee = (m) => (free ? 0 : m.price_cents / 100);

  return (
    <div className="space-y-3" data-testid="shipping-selector">
      <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
        <Truck size={13} className="text-cyber" /> Mode de livraison
        {free && <span className="text-neon">· offerte</span>}
      </p>

      {methods === null ? (
        <div className="flex justify-center py-4"><Loader2 size={18} className="animate-spin text-neon" /></div>
      ) : (
        <div className="space-y-2">
          {methods.map((m) => {
            const active = value?.method?.id === m.id;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => select(m)}
                data-testid={`shipping-method-${m.id}`}
                className={`clip-frame-sm flex w-full items-center justify-between gap-3 border px-4 py-3 text-left transition-all ${
                  active ? 'border-neon/60 bg-neon/10' : 'border-white/10 bg-void/50 hover:border-neon/30'
                }`}
              >
                <span className="flex items-center gap-3">
                  <span className={`flex h-4 w-4 items-center justify-center rounded-full border ${active ? 'border-neon bg-neon' : 'border-white/25'}`}>
                    {active && <CheckCircle2 size={12} className="text-void" />}
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-slate-100">{m.name}</span>
                    <span className="block font-mono text-[9px] uppercase tracking-[0.15em] text-slate-500">{m.carrier}</span>
                  </span>
                </span>
                <span className={`font-mono text-sm font-bold ${free ? 'text-neon' : 'text-slate-200'}`}>
                  {free ? 'Offerte' : `${fee(m).toFixed(2).replace('.', ',')} €`}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <AnimatePresence>
        {value?.method?.mode === 'PARCEL_POINT' && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="border border-cyber/25 bg-void/50 p-4">
              <p className="mb-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-cyber">
                <MapPin size={12} /> Choisir votre point relais
              </p>
              <div className="flex gap-2">
                <input
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), search())}
                  placeholder="Code postal (ex. 75011)"
                  data-testid="relay-zip-input"
                  className="flex-1 border border-white/10 bg-void/60 px-3 py-2.5 font-mono text-sm text-slate-200 placeholder:text-slate-600 focus:border-cyber/60 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={search}
                  disabled={searching}
                  data-testid="relay-search-button"
                  className="clip-frame-sm flex items-center gap-2 border border-cyber/40 px-4 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-cyber transition-colors hover:bg-cyber hover:text-void disabled:opacity-50"
                >
                  {searching ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />} <span className="hidden sm:inline">Chercher</span>
                </button>
              </div>

              {points && points.length > 0 && (
                <div className="mt-3 max-h-44 space-y-2 overflow-y-auto overscroll-contain sm:max-h-48" data-lenis-prevent data-testid="relay-points-list">
                  {points.map((p) => {
                    const activePoint = value?.point?.code === p.code;
                    const loc = p.location || {};
                    const addr = `${loc.number || ''} ${loc.street || ''}`.trim();
                    const pickPoint = () => onChange({
                      ...value,
                      point: { code: p.code, name: p.name, address: addr, city: loc.city, postalCode: loc.postalCode },
                    });
                    return (
                      <button
                        key={p.code}
                        type="button"
                        onClick={pickPoint}
                        data-testid={`relay-point-${p.code}`}
                        className={`flex w-full items-start gap-3 border p-3 text-left transition-all ${
                          activePoint ? 'border-neon/60 bg-neon/10' : 'border-white/10 hover:border-neon/30'
                        }`}
                      >
                        <MapPin size={14} className={`mt-0.5 shrink-0 ${activePoint ? 'text-neon' : 'text-slate-500'}`} />
                        <span>
                          <span className="block text-xs font-semibold text-slate-100">{p.name}</span>
                          <span className="block font-mono text-[10px] text-slate-500">
                            {loc.number} {loc.street}, {loc.postalCode} {loc.city}
                            {p.distance != null && ` · ${(p.distance / 1000).toFixed(1)} km`}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
              {value?.point && (
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-neon" data-testid="relay-selected">
                  ✓ {value.point.name} — {value.point.postalCode} {value.point.city}
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ShippingSelector;
